import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Receipt, FileText, Calendar as CalIcon,
  Heart, AlertTriangle, Car, PiggyBank, BellRing, Settings as Cog,
  LogOut, Menu, X, Wallet, BookOpenCheck, Briefcase, HeartHandshake, UserCircle,
  Inbox, CreditCard, Shield, Stethoscope, MessageCircle, HelpCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/Logo";
import ClientSwitcher from "@/components/ClientSwitcher";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import InstallAppButton from "@/components/InstallAppButton";
import api from "@/lib/api";

const CLIENT_NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/profile", icon: UserCircle, label: "My Profile" },
  { to: "/budget", icon: Wallet, label: "NDIS Budget" },
  { to: "/workers", icon: Users, label: "Support Workers" },
  { to: "/timesheets", icon: BookOpenCheck, label: "Timesheets" },
  { to: "/invoices", icon: Receipt, label: "Invoices" },
  { to: "/roster", icon: CalIcon, label: "Roster" },
  { to: "/vitals", icon: Heart, label: "Vital Signs" },
  { to: "/incidents", icon: AlertTriangle, label: "Incident Log" },
  { to: "/kilometres", icon: Car, label: "Kilometres" },
  { to: "/financial", icon: PiggyBank, label: "Financial Insights" },
  { to: "/payroll-tax", icon: FileText, label: "Payroll Tax" },
  { to: "/plan-manager", icon: Briefcase, label: "Plan Manager" },
  { to: "/medical-staff", icon: Stethoscope, label: "Medical Staff" },
  { to: "/team-chat", icon: MessageCircle, label: "Team Chat", showMessageBadge: true },
  { to: "/family", icon: HeartHandshake, label: "Family & Friends" },
  { to: "/notifications", icon: BellRing, label: "Approvals", showBadge: true },
  { to: "/subscription", icon: CreditCard, label: "Subscription" },
  { to: "/help", icon: HelpCircle, label: "Help" },
  { to: "/settings", icon: Cog, label: "Settings" },
];

const WORKER_NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/inbox", icon: Inbox, label: "Inbox" },
  { to: "/my-profile", icon: UserCircle, label: "My Profile" },
  { to: "/my-clients", icon: Users, label: "My Clients" },
  { to: "/timesheets", icon: BookOpenCheck, label: "My Timesheets" },
  { to: "/invoices", icon: Receipt, label: "My Invoices" },
  { to: "/roster", icon: CalIcon, label: "My Roster" },
  { to: "/vitals", icon: Heart, label: "Log Vitals" },
  { to: "/incidents", icon: AlertTriangle, label: "Incidents" },
  { to: "/kilometres", icon: Car, label: "Kilometres" },
  { to: "/medical-staff", icon: Stethoscope, label: "Medical Staff" },
  { to: "/team-chat", icon: MessageCircle, label: "Team Chat", showMessageBadge: true },
  { to: "/subscription", icon: CreditCard, label: "Subscription" },
  { to: "/help", icon: HelpCircle, label: "Help" },
  { to: "/settings", icon: Cog, label: "Settings" },
];

export default function Layout({ children }) {
  const { user, logout, subscription, unreadMessages, refreshUnreadMessages } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const baseItems = user?.role === "client" ? CLIENT_NAV : WORKER_NAV;
  // Inject Admin nav for admin accounts (between Subscription and Settings)
  const items = subscription?.is_admin
    ? [...baseItems.slice(0, -1), { to: "/admin", icon: Shield, label: "Admin" }, baseItems[baseItems.length - 1]]
    : baseItems;
  const roleColor = user?.role === "client" ? "client" : "worker";

  useEffect(() => {
    if (user?.role !== "client") return;
    let alive = true;
    const refresh = async () => {
      try {
        const { data } = await api.get("/notifications/pending-count");
        if (alive) setPendingCount(data.count || 0);
      } catch { /* ignore — transient network blips shouldn't spam toasts */ }
    };
    refresh();
    // 60s is plenty for a badge counter; was 15s which was hammering the server
    // and re-firing on every navigation (loc.pathname dep removed below).
    const id = setInterval(refresh, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Poll unread messages every 60s for sidebar badge
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const tick = async () => { if (alive) await refreshUnreadMessages(); };
    tick();
    const id = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user, refreshUnreadMessages]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-layout">
      {/* role colour strip */}
      <div className={user?.role === "worker" ? "brand-strip-worker" : "brand-strip"} />

      <div className="flex flex-1 min-h-0">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:flex w-72 shrink-0 flex-col border-r bg-card sticky top-0 h-screen" data-testid="sidebar">
          <div className="p-5 border-b">
            <Logo showFullName />
          </div>
          <ClientSwitcher />
          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            {items.map((it) => {
              const active = loc.pathname === it.to;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`flex items-center gap-3 rounded-2xl px-4 min-h-[48px] text-sm font-semibold transition-all ${
                    active
                      ? `bg-${roleColor}/15 text-${roleColor} ring-2 ring-${roleColor}/30`
                      : "hover:bg-muted text-foreground/80 hover:text-foreground"
                  }`}
                  style={active ? { backgroundColor: `hsl(var(--${roleColor}) / 0.12)`, color: `hsl(var(--${roleColor}))` } : {}}
                >
                  <it.icon className="w-5 h-5 shrink-0" strokeWidth={2.4} />
                  <span className="flex-1">{it.label}</span>
                  {it.showBadge && pendingCount > 0 && (
                    <Badge className="rounded-full bg-coral text-white" data-testid="approvals-badge">{pendingCount}</Badge>
                  )}
                  {it.showMessageBadge && unreadMessages > 0 && (
                    <Badge className="rounded-full bg-coral text-white" data-testid="messages-badge">{unreadMessages}</Badge>
                  )}
                </Link>
              );
            })}
          </nav>
          <InstallAppButton />
          <div className="p-4 border-t flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-client/30 bg-muted shrink-0 grid place-items-center font-bold text-client text-sm" data-testid="sidebar-avatar">
              {user?.avatar_url
                ? <img src={user.avatar_url} alt="me" className="w-full h-full object-cover" />
                : (user?.full_name || "?")[0]}
            </div>
            <div className="flex-1 min-w-0 text-xs">
              <div className="font-bold truncate" data-testid="current-user-name">{user?.full_name}</div>
              <div className="uppercase tracking-wider text-[10px] text-muted-foreground" data-testid="current-user-role">{user?.role}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </aside>

        {/* Mobile top bar — respects iOS safe-area so the menu button isn't hidden behind the status bar */}
        <div
          className="lg:hidden fixed top-0 inset-x-0 z-40 px-4 pb-3 bg-card/95 backdrop-blur border-b flex items-center justify-between"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}
        >
          <Logo />
          <div className="flex items-center gap-2">
            {user?.role === "client" && pendingCount > 0 && (
              <Badge className="rounded-full bg-coral text-white" data-testid="mobile-approvals-badge">{pendingCount}</Badge>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="h-11 w-11"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-30 bg-black/50" onClick={() => setMobileOpen(false)}>
            <div
              className="absolute inset-x-3 rounded-3xl bg-card shadow-2xl p-3 max-h-[75vh] overflow-y-auto"
              style={{ top: "calc(env(safe-area-inset-top, 0px) + 64px)" }}
              onClick={(e) => e.stopPropagation()}
            >
              {items.map((it) => (
                <Link key={it.to} to={it.to}
                  className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold hover:bg-muted"
                  data-testid={`mobile-nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <it.icon className="w-5 h-5" /> {it.label}
                </Link>
              ))}
              <button onClick={() => { logout(); nav("/login"); }} data-testid="mobile-logout-btn"
                className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold w-full text-left hover:bg-muted text-destructive">
                <LogOut className="w-5 h-5" /> Sign out
              </button>
            </div>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 min-w-0 relative" data-testid="main-content">
          <SubscriptionBanner />
          <div className="lg:p-8 p-4 pt-[calc(env(safe-area-inset-top,0px)+5rem)] lg:pt-8 relative">
            <div className="blob animate-blob" style={{ width: 380, height: 380, top: -60, right: -120, background: "hsl(var(--client) / 0.18)" }} />
            <div className="blob animate-blob" style={{ width: 280, height: 280, bottom: -80, left: -80, background: "hsl(var(--coral) / 0.15)", animationDelay: "-7s" }} />
            <div className="relative z-10">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
